/**
 * The direct trade's state machine, its deadlines, and its audit vocabulary.
 *
 * Platform-neutral and pure: the server owns every transition and every clock
 * reading, and this module only says which transitions exist. Stated as data
 * rather than as `if` chains scattered through a service, so "a locked trade
 * cannot go back to open" is one fact that a test can read.
 */

export const TRADE_STATES = [
  /** Sent, not yet answered. */
  'invited',
  /** Both are in it; offers and fee contributions may change. */
  'open',
  /** Both accepted the SAME revision; nothing may change, only confirm. */
  'locked',
  /** Settled exactly once. Terminal. */
  'completed',
  /** The invitee said no. Terminal. */
  'declined',
  /** Either side stopped it. Terminal. */
  'cancelled',
  /** A deadline passed. Terminal. */
  'expired',
] as const;
export type TradeState = (typeof TRADE_STATES)[number];

export const TERMINAL_TRADE_STATES: readonly TradeState[] = Object.freeze([
  'completed',
  'declined',
  'cancelled',
  'expired',
]);

export function isTerminalTradeState(state: TradeState): boolean {
  return TERMINAL_TRADE_STATES.includes(state);
}

/**
 * Every transition that exists. Anything absent is a refusal.
 *
 * Note what `locked` can NOT do: it cannot return to `open`. Once both sides
 * have accepted the same revision the offer is frozen, and a path back would
 * mean somebody could accept a trade and then change what they were giving.
 * `locked -> cancelled` exists only for an authoritative failure discovered
 * BEFORE the commit (a VIP that lapsed, a balance that moved, an item that left)
 * and for a participant who backs out before confirming — never as an edit.
 */
const TRANSITIONS: Readonly<Record<TradeState, readonly TradeState[]>> = Object.freeze({
  invited: Object.freeze(['open', 'declined', 'cancelled', 'expired'] as const),
  open: Object.freeze(['locked', 'cancelled', 'expired'] as const),
  locked: Object.freeze(['completed', 'cancelled', 'expired'] as const),
  completed: Object.freeze([] as const),
  declined: Object.freeze([] as const),
  cancelled: Object.freeze([] as const),
  expired: Object.freeze([] as const),
});

export function canTransition(from: TradeState, to: TradeState): boolean {
  return TRANSITIONS[from].includes(to);
}

/** Which states still allow the offer or the fee contribution to change. */
export function isEditable(state: TradeState): boolean {
  return state === 'open';
}

/* -------------------------------------------------------------------------- */
/*  Deadlines                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * How long each phase lasts, measured on the SERVER's clock.
 *
 * The invitation is short because an unanswered one holds nothing and should
 * not linger in an inbox. The open phase is long because building an offer is
 * deliberate work. The locked phase is short again because at that point both
 * players are looking at the same frozen summary and the only thing left is a
 * click — and every second of it is a second somebody's Gold is reserved.
 */
export const TRADE_INVITE_TTL_MS = 2 * 60_000;
export const TRADE_OPEN_IDLE_TTL_MS = 15 * 60_000;
export const TRADE_LOCK_TTL_MS = 2 * 60_000;

/** The deadline a trade in this state runs on, from its last activity. */
export function tradeDeadlineMs(state: TradeState): number | null {
  switch (state) {
    case 'invited':
      return TRADE_INVITE_TTL_MS;
    case 'open':
      return TRADE_OPEN_IDLE_TTL_MS;
    case 'locked':
      return TRADE_LOCK_TTL_MS;
    default:
      return null;
  }
}

/* -------------------------------------------------------------------------- */
/*  Audit                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Everything worth recording, append-only.
 *
 * `settlement_failed` is in the list on purpose: a settlement that rolled back
 * transferred nothing and charged nothing, but the ATTEMPT is exactly the thing
 * somebody investigating a complaint needs to see. Silence would look identical
 * to a trade that was never tried.
 */
export const TRADE_EVENT_TYPES = [
  'invited',
  'accepted',
  'declined',
  'offer_changed',
  'fee_contribution_changed',
  'offer_accepted',
  'locked',
  'empty_side_acknowledged',
  'final_confirmed',
  'completed',
  'cancelled',
  'expired',
  'settlement_failed',
] as const;
export type TradeEventType = (typeof TRADE_EVENT_TYPES)[number];

/**
 * Why a currency hold exists.
 *
 * Offer and fee are separate on purpose and must never be merged. Gold offered
 * to the other player arrives whole; Gold committed to the fee is destroyed. A
 * single "reserved" bucket could not tell a refund from a burn.
 */
export const CURRENCY_RESERVATION_PURPOSES = ['direct_trade_offer', 'direct_trade_fee'] as const;
export type CurrencyReservationPurpose = (typeof CURRENCY_RESERVATION_PURPOSES)[number];

export const CURRENCY_RESERVATION_STATUSES = ['active', 'released', 'consumed'] as const;
export type CurrencyReservationStatus = (typeof CURRENCY_RESERVATION_STATUSES)[number];
